import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Search, LifeBuoy } from "lucide-react";

const MODES = [
  { key: "name", label: "Nom & prénom", placeholder: "ex. Marie Dupont", type: "text", hint: "tels qu’indiqués sur Booking ou Airbnb" },
  { key: "phone", label: "Téléphone", placeholder: "06 12 34 56 78", type: "tel", hint: "celui donné lors de la réservation" },
  { key: "email", label: "Email", placeholder: "vous@exemple.fr", type: "email", hint: "réservation directe ou Lodgify" },
];

const EMPTY = { name: "", phone: "", email: "", check_in: "" };

export function ClaimBooking({ onClaimed }) {
  const [form, setForm] = useState(EMPTY);
  const [mode, setMode] = useState("name");
  const [params] = useSearchParams();
  const fromQr = params.get("claim") === "1";
  useEffect(() => {
    if (fromQr) {
      const today = new Date().toISOString().slice(0, 10);
      setForm((f) => ({ ...f, check_in: f.check_in || today }));
      setTimeout(() => document.getElementById("claim-booking")?.scrollIntoView({ behavior: "smooth" }), 200);
    }
  }, [fromQr]);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [helpSent, setHelpSent] = useState(false);
  const navigate = useNavigate();
  const current = MODES.find((m) => m.key === mode);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/bookings/claim", { check_in: form.check_in, [mode]: form[mode] });
      toast.success(`${data.claimed} séjour(s) retrouvé(s) et associé(s) à votre compte`);
      setForm(EMPTY); setFailed(false); onClaimed?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setFailed(true); }
    finally { setBusy(false); }
  };
  const askHelp = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/bookings/claim/help", { check_in: form.check_in, [mode]: form[mode], listing_id: params.get("listing") || "" });
      setHelpSent(true);
      toast.success("Message envoyé à l’équipe, nous associons votre séjour rapidement.", { action: { label: "Voir", onClick: () => navigate("/messagerie") } });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <form id="claim-booking" onSubmit={submit} className={`rounded-2xl bg-white border p-5 ${fromQr ? "border-rose-brand ring-2 ring-rose-brand/30" : "border-border"}`} data-testid="claim-booking">
      <p className="font-semibold text-olive flex items-center gap-2"><Search className="w-4 h-4 text-rose-brand" /> {fromQr ? "Bienvenue ! Retrouvez votre séjour" : "Retrouver mon séjour"}</p>
      <p className="text-sm text-muted-foreground mt-1">Réservé via Booking, Airbnb ou notre site ? Indiquez votre date d’arrivée et l’une de ces informations.</p>
      <div className="flex gap-2 mt-3 flex-wrap" role="tablist">
        {MODES.map((m) => (
          <button key={m.key} type="button" onClick={() => setMode(m.key)} data-testid={`claim-mode-${m.key}`}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${mode === m.key ? "bg-olive text-white border-olive" : "border-border text-muted-foreground hover:border-olive"}`}>
            {m.label}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3 items-end">
        <div>
          <Label className="text-xs">{current.label} <span className="text-muted-foreground font-normal">— {current.hint}</span></Label>
          <Input type={current.type} required placeholder={current.placeholder} value={form[mode]} onChange={(e) => setForm({ ...form, [mode]: e.target.value })} data-testid={`claim-${mode}`} />
        </div>
        <div><Label className="text-xs">Date d’arrivée</Label><Input type="date" required value={form.check_in} onChange={(e) => setForm({ ...form, check_in: e.target.value })} data-testid="claim-checkin" /></div>
        <Button type="submit" disabled={busy} className="bg-olive text-white" data-testid="claim-submit">{busy ? "..." : "Retrouver"}</Button>
      </div>
      {failed && !helpSent && (
        <div className="mt-3 flex items-center gap-3 flex-wrap text-sm" data-testid="claim-help-block">
          <span className="text-muted-foreground">Toujours rien ?</span>
          <button type="button" onClick={askHelp} disabled={busy || !form.check_in} className="text-rose-brand font-semibold hover:underline flex items-center gap-1" data-testid="claim-help-button">
            <LifeBuoy className="w-4 h-4" /> Je ne retrouve pas mon séjour — prévenir l’équipe
          </button>
        </div>
      )}
      {helpSent && <p className="mt-3 text-sm text-olive" data-testid="claim-help-sent">✓ L’équipe a été prévenue et vous répondra dans la messagerie.</p>}
    </form>
  );
}
